"use strict";
/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.Err = exports.Ok = void 0;
/**
 * Base class for Result types
 *
 * @template ValueType - The type of the value in the result
 * @template ErrorType - The type of the error in the result
 */
class BaseResult {
    /**
     * Extract the OK value from result.
     *
     * @returns A value of type {@link ValueType} if the result is {@link Ok}, otherwise returns `null`.
     *
     * @example
     * ```ts
     * let result = new Ok(1);
     * let value = result.getOk(); // value == 1
     * ```
     *
     * @example
     * ```ts
     * let result = new Err(new Error('Should return null'))
     * let value = result.getOk(); // value == null
     * ```
     */
    getOk() {
        if (this.isOk()) {
            return this.value;
        }
        if (this.isErr()) {
            return null;
        }
        // This should never happen
        throw new Error(`Result was neither Ok nor Err - ${this}`);
    }
    /** Extract the Err value from the result
     *
     * @returns A value of type {@link ErrorType} if the result is {@link Err}, otherwise returns `null`.
     *
     * @example
     * ```ts
     * let result = new Ok(1);
     * let error = result.getErr(); // error == null
     * ```
     *
     * @example
     * ```ts
     * let result = new Err(new Error('Should not be null'))
     * let error = result.getErr(); // error.message == "Should not be null"
     * ```
     */
    getErr() {
        if (this.isErr()) {
            return this.error;
        }
        if (this.isOk()) {
            return null;
        }
        // This should never happen
        throw new Error(`Result was neither Ok nor Err - ${this}`);
    }
    /**
     * Returns the value if the result is successful, otherwise returns the provided default value.
     *
     * @param fallback The default value to return if the result is unsuccessful.
     * @returns The value of the result if successful, otherwise the provided default value.
     *
     * @example
     * ```ts
     * const result = new Ok(1);
     * console.log(result.getOr(0)); // 1
     * ```
     *
     * @example
     * ```ts
     * const result = new Err(new Error('Not Found'));
     * console.log(result.getOr(0)); // 0
     * ```
     */
    getOr(fallback) {
        return this.isOk() ? this.value : fallback;
    }
    /**
     * Generates a fallback value using the provided function if the result is unsuccessful.
     *
     * @param generateFallback The function to generate a fallback value.
     * @returns The value of the result if successful, otherwise the generated fallback value.
     *
     * @example
     * ```ts
     * const result = new Ok(1);
     * console.log(result.getOrElse(() => 0)); // 1
     * ```
     *
     * @example
     * ```ts
     * const result = new Err(new Error('Not Found'));
     * console.log(result.getOrElse((error) => error.message)); // 'Not Found'
     * ```
     */
    getOrElse(generateFallback) {
        if (this.isOk()) {
            return this.value;
        }
        if (this.isErr()) {
            return generateFallback(this.error);
        }
        // This should never happen
        throw new Error(`Result was neither Ok nor Err - ${this}`);
    }
    /**
     * Returns the value if the result is successful, otherwise throws the error.
     *
     *
     * @returns Value contained in the Result if it is an instance of {@link Ok}
     * @throws {ErrorType} - Error contained within the Result if it is an
     * instance of {@link Err}.
     *
     * @deprecated to indicate that this function is only to be used to make migration
     * easier and all usages should eventually be removed.
     *
     * @example
     * ```ts
     * const result = new Ok(1);
     * try {
     *   let value = result.getOrThrow();
     *   console.log(value); // Prints: 1
     * } catch(err) {
     *   // Code never reaches here
     * }
     * ```
     *
     * @example
     * ```ts
     * const result = new Err(new Error("This should be thrown"));
     *
     * try {
     *   let value = result.getOrThrow();
     *   console.log(value); // Code never reaches here
     * } catch(err) {
     *   console.log(err.message); // Prints: "This should be thrown"
     * }
     *
     * ```
     */
    getOrThrow() {
        if (this.isOk()) {
            return this.value;
        }
        if (this.isErr()) {
            throw this.error;
        }
        // This should never happen
        throw new Error(`Result was neither Ok nor Err - ${this}`);
    }
}
/**
 * Represents a successful result.
 *
 * @template ValueType - The type of the value in the result
 * @template ErrorType - The type of the error in the result
 */
class Ok extends BaseResult {
    constructor(value) {
        super();
        this.value = value;
    }
    isOk() {
        return true;
    }
    isErr() {
        return false;
    }
    toTuple() {
        return [this.value, null];
    }
    map(valueTransform) {
        return new Ok(valueTransform(this.value));
    }
    mapErr(_errorTransform) {
        // This is a no-op since the result is always Ok
        return this;
    }
}
exports.Ok = Ok;
class Err extends BaseResult {
    constructor(error) {
        super();
        this.error = error;
    }
    isOk() {
        return false;
    }
    isErr() {
        return true;
    }
    toTuple() {
        return [null, this.error];
    }
    map(_valueTransform) {
        // This is a no-op since the result is always Err
        return this;
    }
    mapErr(errorTransform) {
        return new Err(errorTransform(this.error));
    }
}
exports.Err = Err;
//# sourceMappingURL=Result.js.map